import React, { useEffect, useRef, useState } from 'react';
import { Check, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MAX_NOTE_CHARS, type MemoryNote } from '@/lib/writer/memoryClient';

interface Props {
  note: MemoryNote;
  onSave: (id: string, text: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * One memory note, read first and editable on click. Reads like a margin
 * note, not a form field.
 */
const MemoryNoteRow: React.FC<Props> = ({ note, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.note);
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(note.note);
  }, [note.note]);

  useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  const commit = async () => {
    const text = draft.trim();
    if (!text || text === note.note) {
      setEditing(false);
      setDraft(note.note);
      return;
    }
    setBusy(true);
    try {
      await onSave(note.id, text);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await onDelete(note.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="group flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-muted/50 transition-colors">
      <div className="flex-1 min-w-0 space-y-1">
        {editing ? (
          <Textarea
            ref={textareaRef}
            value={draft}
            maxLength={MAX_NOTE_CHARS}
            rows={2}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void commit();
              }
              if (e.key === 'Escape') {
                setEditing(false);
                setDraft(note.note);
              }
            }}
            className="text-sm"
            aria-label="Edit note"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-left text-sm leading-relaxed text-foreground w-full"
          >
            {note.note}
          </button>
        )}
        <p className="text-[11px] text-muted-foreground">
          {note.citekey ? (
            <span className="mr-2 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
              {note.citekey}
            </span>
          ) : null}
          {note.origin === 'author' ? 'You' : 'Writer'}
          {note.created_at ? ` · ${formatDate(note.created_at)}` : ''}
        </p>
      </div>

      <div className="flex items-center gap-1 pt-0.5">
        {editing ? (
          <>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={busy}
              aria-label="Save note"
              onClick={() => void commit()}
            >
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              aria-label="Cancel editing"
              onClick={() => {
                setEditing(false);
                setDraft(note.note);
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
            disabled={busy}
            aria-label="Delete note"
            onClick={() => void remove()}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </li>
  );
};

export default MemoryNoteRow;
